import {mountFloatingVueInstances, unmountFloatingVueInstances} from './vue-manager.ts'
import * as Sentry from '@sentry/vue'
//load only if all libs are loaded
import type {TableAction} from './types/table-types.ts'
import './utils/alert-utils.ts'
import {axiosClient, getCsrfFromInput} from './utils/axios-client.ts'
import axios, {AxiosError, AxiosHeaders, type AxiosResponse, isAxiosError, type RawAxiosRequestHeaders} from 'axios'
import {formDataToObject, normalizeCheckboxes} from './utils/data-utils.ts'
import Swal from "sweetalert2";

type CallbackHandler = (...args: unknown[]) => void

const X_XSRF_TOKEN_HEADER = 'X-XSRF-TOKEN' as const
const SESSION_OPEN_CHECK_URL = '/web/public/session/open' as const

let isListening = false
let authRedirectInProgress = false
const registeredCallbacks: { fn: CallbackHandler; params: unknown[] }[] = []
const originalFetch = window.fetch

Sentry.init({
    release: import.meta.env.MODE,
    environment: import.meta.env.MODE,
    dsn: 'https://551f8f7adc6b0d991afc01db8e11564b@o4510330414301184.ingest.de.sentry.io/4510459026145360',
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', 'giztest.lzp.nl:6443', 'giz.lzp.nl'],
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
})
axiosClient.interceptors.response.use(
    response => response,
    async error => {
        await handleAuthError(
            error.response?.status ?? 0,
            (error.response?.request as any)?.responseURL,
            error.config?.url
        )
        return Promise.reject(error)
    }
);
axiosClient.interceptors.request.use(
    (config) => {
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);
axios.interceptors.request.use(
    (config) => {
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const checkBackendHealth = async (
    baseUrl: string = ''
): Promise<boolean> => {
    try {
        const response = await fetch(`${baseUrl}/actuator/health`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        })

        if (!response.ok) {
            return false
        }

        const data = await response.json()

        return data?.status === 'UP'
    } catch (e) {
        return false
    }
}

export const handleBackendConnectionIssue = (): void => {
    checkBackendHealth()
        .then((isUp) => {
            if (!isUp) {
                window.Toast.fire({
                    icon: 'error',
                    title: 'Geen verbinding met de server',
                    text: 'Controleer of de server draait of de URL klopt'
                })
            } else {
                window.Toast.fire({
                    icon: 'error',
                    title: 'Er is een serverfout opgetrede'
                })
            }
        })
        .catch(() => {
            window.Toast.fire({
                icon: 'error',
                title: 'Server niet bereikbaar',
                text: 'Health check mislukt'
            })
        })
}

function executeCallbacks() {
    while (registeredCallbacks.length > 0) {
        const callback = registeredCallbacks.shift()
        if (!callback) break
        callback.fn(...callback.params)
    }

    const element = document.getElementsByClassName('spinner')[0]
    element?.remove()
}

function startListening(targetFunction: CallbackHandler, ...params: unknown[]) {
    if (registeredCallbacks.filter(h => h.fn === targetFunction).length == 0) {
        registeredCallbacks.push({fn: targetFunction, params})
    }

    if (document.readyState === 'complete') {
        executeCallbacks()
        return
    }

    if (isListening) return
    isListening = true

    function handler() {
        if (document.readyState === 'complete') {
            document.removeEventListener('readystatechange', handler)
            executeCallbacks()

            isListening = false
        }
    }

    document.addEventListener('readystatechange', handler)
}

const handleAxiosError = (
    error: unknown,
    btn?: JQuery<HTMLElement> | null | undefined,
    errorCallback?: (error: unknown) => void | undefined,
): void => {
    if (isAxiosError(error)) {
        if (error.response) {
            if (errorCallback != undefined) {
                errorCallback(error.response.data)
            } else {
                void window.showRequestError(error.response.data)
            }
        } else {
            if (
                error.code === 'ERR_NETWORK' ||
                error.message.includes('ERR_CONNECTION_REFUSED')
            ) {
                handleBackendConnectionIssue()
            }
        }
    } else {
        window.Toast.fire({
            icon: 'error',
            title: 'Er is een serverfout opgetrede',
        })
    }

    if (btn != undefined && btn) {
        removeSpinner(btn)
    }
}

window.handleJQueryError = (err: JQuery.jqXHR, title: string | undefined, message: string | undefined): void => {
    const status = err.status
    if (status === 0) {
        handleBackendConnectionIssue()
        return
    }

    if (status === 401 || status === 403) {
        void handleAuthError(status, (err as any).responseURL).then((redirected) => {
            if (!redirected) {
                Swal.fire({
                    title: (typeof title != undefined && title != null) ? title : "Fout",
                    html: (typeof message != undefined && message != null) ? message : (err.responseText ?? "Onbekende fout"),
                    icon: "error",
                    confirmButtonText: "OK"
                })
            }
        })
        return
    }

    Swal.fire({
        title: (typeof title != undefined && title != null) ? title : "Fout",
        html: (typeof message != undefined && message != null) ? message : (err.responseText ?? "Onbekende fout"),
        icon: "error",
        confirmButtonText: "OK"
    })
}


window.loadScriptAfterReadyState = (
    targetFunction: (...args: unknown[]) => void,
    ...params: unknown[]
) => {
    startListening(targetFunction, ...params)
}

document.addEventListener('DOMContentLoaded', function () {
    document.body.addEventListener('click', function (event: PointerEvent) {
        const eventTarget: HTMLElement | null = event.target as HTMLElement | null

        if (!eventTarget) return

        const link: HTMLAnchorElement | null = eventTarget.closest('a.giz-link')
        if (!link) return

        const template = link.getAttribute('template')
        const url = link.getAttribute('src')
        const target = link.getAttribute('target')

        if (!template || !url || !target) return
        window.loadAjaxPage(url, target, template, undefined)
        localStorage.setItem('current-page', JSON.stringify([url, target, template]))
    })
})

function handleHtmlResponse(
    response: { data: string },
    target: string,
    name: string
): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(response.data, 'text/html');

    const isLogin =
        doc.querySelector('meta[name="login"]')?.getAttribute('content') === 'true';

    if (isLogin) {
        window.location.href = `/web/login?page=${name}`;
        return;
    }

    $(target).html(response.data);
}

async function loadAjaxPageFromHistory(event: PopStateEvent) {
    let response: AxiosResponse
    const target = event.state.target
    const name = event.state.name
    const data = event.state.data
    try {
        response = await axiosClient.request({
            url: event.state.url + '?page=' + name,
            method: 'GET',
            params: data,
        })
    } catch (error) {
        handleAxiosError(error)
        return
    }

    unmountFloatingVueInstances()
    handleHtmlResponse(response, target, name);
    mountFloatingVueInstances()
}

window.addEventListener('popstate', async function (event: PopStateEvent) {
    if (event.state) {
        await loadAjaxPageFromHistory(event)
    }
})

window.loadLastVisitedPage = function () {
    let page = ''
    let target = '#content'
    let url = '/web/plattegrond'
    const currentPage = localStorage.getItem('current-page')
    if (currentPage) {
        const parsed = JSON.parse(currentPage)
        if (Array.isArray(parsed) && parsed.length == 3) {
            const [prevUrl, prevTarget, prevPage] = parsed as [string, string, string]
            page = prevPage
            target = prevTarget
            url = prevUrl
        }
    }
    window.loadScriptAfterReadyState(window.loadAjaxPage as CallbackHandler, url, target, page)
}

function initTableMenu(tableId: string, actions: TableAction[]): BootstrapMenu {
    return new BootstrapMenu(`#${tableId} tbody tr`, {
        fetchElementData: function ($rowElem: unknown) {
            const rowdata = $('#' + tableId)
                .DataTable()
                .row($rowElem)
                .data()
            return {rowdata, tableId}
        },
        actions,
    })
}

window.setMenu = (tablId: string, actions: TableAction[]): BootstrapMenu => {
    $('.' + (tablId + '-bootstrap-menu')).remove()
    const menu = initTableMenu(tablId, actions)
    menu.$menu.addClass(tablId + '-bootstrap-menu')

    return menu
}

window.loadAjaxPage = async (
    url: string,
    target: string,
    name: string,
    data: object | undefined
) => {
    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            url: url,
            method: 'GET',
            params: data,
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse)
        return
    }
    history.pushState({url: url, data, target, name}, '', '?page=' + name)
    unmountFloatingVueInstances()
    handleHtmlResponse(response, target, name);
    mountFloatingVueInstances()
}

window.POST = async (
    url: string,
    data: unknown | string,
    params: unknown | string,
    successCallback?: (data: unknown) => void | undefined,
    errorCallback?: (error: unknown) => void | undefined,
    headers?: AxiosHeaders | RawAxiosRequestHeaders
) => {
    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            url: url,
            method: 'POST',
            params,
            data,
            headers: {[X_XSRF_TOKEN_HEADER]: getCsrfFromInput(), ...headers},
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse, null, errorCallback)
        return
    }
    if (successCallback != undefined) successCallback(response.data)
}

window.DELETE = async (
    url: string,
    data: unknown | string,
    params: unknown | string,
    successCallback?: (data: unknown) => void | undefined,
    errorCallback?: (error: unknown) => void | undefined,
    headers?: AxiosHeaders | RawAxiosRequestHeaders
) => {
    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            url: url,
            method: 'DELETE',
            params,
            data,
            headers: {[X_XSRF_TOKEN_HEADER]: getCsrfFromInput(), ...headers},
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse, null, errorCallback)
        return
    }
    if (successCallback != undefined) successCallback(response.data)
}

window.PUT = async (
    url: string,
    data: unknown | string,
    params: unknown | string,
    successCallback?: (data: unknown) => void | undefined,
    errorCallback?: (error: unknown) => void | undefined,
    headers?: AxiosHeaders | RawAxiosRequestHeaders
) => {
    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            url: url,
            method: 'PUT',
            params,
            data,
            headers: {[X_XSRF_TOKEN_HEADER]: getCsrfFromInput(), ...headers},
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse, null, errorCallback)
        return
    }
    if (successCallback != undefined) successCallback(response.data)
}

function addSpinner(elm: JQuery<HTMLElement>) {
    elm.find('.fa-spinner').remove()
    elm.prepend('<i class="fa fa-spinner fa-spin fa-3x fa-fw w3-medium submit-spinner"></i>')
}

function removeSpinner(elm: JQuery<HTMLElement>) {
    const s = elm.find('.fa-spinner')
    s.closest('button').prop('disabled', false)
    s.remove()
}

window.submitFormAsJson = async (
    formSelector: JQuery<HTMLFormElement>,
    success?: (data: unknown) => void | undefined
) => {
    const form = $(formSelector)
    const actionUrl = form.attr('action')
    normalizeCheckboxes(form[0])
    const formData = new FormData(form[0])
    const data = formDataToObject(formData)
    const btn = form.parents('.modal-dialog').find(`.modal-footer .btn-success`)
    btn.prop('disabled', 'true')
    addSpinner(btn)

    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            method: 'POST',
            url: actionUrl,
            data: data,
            headers: {[X_XSRF_TOKEN_HEADER]: getCsrfFromInput()},
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse, btn)
        return
    }

    if (success != undefined) success(response.data)

    removeSpinner(btn)
}

window.submitForm = async (
    formSelector: JQuery<HTMLFormElement>,
    success?: (data: unknown) => void | undefined
) => {
    const form = $(formSelector)
    const actionUrl = form.attr('action')
    const formData = new FormData(form[0])
    const btn = form.parents('.modal-dialog').find(`.modal-footer .btn-success`)
    btn.prop('disabled', 'true')
    addSpinner(btn)

    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            method: 'POST',
            url: actionUrl,
            data: formData,
            headers: {[X_XSRF_TOKEN_HEADER]: getCsrfFromInput()},
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse, btn)
        return
    }

    if (success != undefined) success(response.data)

    removeSpinner(btn)
}

window.GET = async (
    url: string,
    data: string | JQuery.PlainObject | undefined,
    success?: (response: unknown) => void | undefined
) => {
    let response: AxiosResponse
    try {
        response = await axiosClient.request({
            method: 'GET',
            url,
            params: data,
        })
    } catch (errorResponse) {
        handleAxiosError(errorResponse)
        return
    }

    if (success != undefined) success(response.data)
}

window.terrein_canvas = undefined

document.addEventListener('DOMContentLoaded', function () {
    /* Sidebar toggle behaviour */
    $('.sidebar-toggle').on('click touch', function () {
        $('.sidebar').toggleClass('toggled')
        if (typeof window.terrein_canvas != 'undefined') {
            if (window.terrein_canvas) {
                window.onresize()
            }
        }
    })

    const active = $('.sidebar .active')

    if (active.length && active.parent('.collapse').length) {
        const parent = active.parent('.collapse')

        parent.prev('a').attr('aria-expanded', 'true')
        parent.addClass('show')
    }
})

window.applyPrefilled = async function (inputsSelector: string, feature: string) {
    const response = await axiosClient.request({
        method: 'GET',
        url: `/api/v1/ui-state/${feature}`,
    })

    let data: Record<string, string> = response.data;
    if (data == null) {
        const input = localStorage.getItem(`${feature}.${inputsSelector}`)
        if (input == null) return
        data = JSON.parse(input);
    }
    for (const key in data) {
        $(`:input[name="${key}"]`).val(data[key])
    }
}

window.storePrefilled = async function (inputsSelector: string, feature: string) {
    const inputs = $(`:input[name^="${inputsSelector}."], :input[name="${inputsSelector}"]`)
    const output: Record<string, string | string[] | number> = {}
    for (let i = 0; i < inputs.length; i++) {
        const input = $(inputs[i])
        const name = input.attr('name')
        const value = input.val()
        if (value == undefined || name === undefined) continue

        output[name] = value
    }

    localStorage.setItem(`${feature}.${inputsSelector}`, JSON.stringify(output))

    await axiosClient.request({
        method: 'POST',
        url: `/api/v1/ui-state/${feature}`,
        data: output
    })
}

async function checkOpenSession(): Promise<boolean> {
    try {
        const response = await originalFetch(SESSION_OPEN_CHECK_URL, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        })

        if (!response.ok) {
            return false
        }

        const payload = await response.json() as { openSession?: boolean }
        return payload.openSession === true
    } catch {
        return false
    }
}

async function handleAuthError(status: number, responseURL?: string, fallbackURL?: string) {
    const url = responseURL || fallbackURL;
    if (!(status === 401 || status === 403 || status === 302 || (url && url.includes('/login')))) {
        return false
    }

    const openSession = await checkOpenSession()
    if (!openSession && !authRedirectInProgress) {
        authRedirectInProgress = true
        ;(window.top ?? window).location.href = '/web/login'
        return true
    }

    return false
}

// Axios
axios.interceptors.response.use(
    response => response,
    async error => {
        await handleAuthError(
            error.response?.status ?? 0,
            (error.response?.request as any)?.responseURL,
            error.config?.url
        )
        return Promise.reject(error)
    }
);

// Fetch
window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);
    await handleAuthError(response.status, response.url, args[0] as string);
    return response;
};
